(() => {
  'use strict';

  const ROOT_SELECTOR =
    '[data-boho-shop-look]';


  class BohoQuickView {

    constructor(root) {

      this.root = root;

      this.quickView =
        this.root.querySelector(
          '[data-quick-view]'
        );

      this.quickViewBody =
        this.root.querySelector(
          '[data-quick-view-body]'
        );

      this.lastQuickViewTrigger =
        null;


      if (
        !this.quickView ||
        !this.quickViewBody
      ) {
        return;
      }


      this.bindEvents();
    }



    /* ============================================================
       PAGE LOCK
       ============================================================ */

    lockPage() {

      document.documentElement.style.overflow =
        'hidden';

      document.body.style.overflow =
        'hidden';
    }


    unlockPage() {

      document.documentElement.style.overflow =
        '';

      document.body.style.overflow =
        '';
    }



    /* ============================================================
       OPEN
       ============================================================ */

    openQuickView(trigger) {

      if (
        !trigger ||
        !this.quickView ||
        !this.quickViewBody
      ) {
        return;
      }


      const item =
        trigger.closest(
          '[data-look-item]'
        );


      if (!item) {
        return;
      }


      const template =
        item.querySelector(
          '[data-quick-view-template]'
        );


      if (!template) {

        console.warn(
          'Boho Tales Quick View: product template not found.'
        );

        return;
      }


      this.lastQuickViewTrigger =
        trigger;


      /*
        Reset modal content.
      */

      this.quickViewBody.innerHTML =
        '';


      /*
        Clone selected product.
      */

      this.quickViewBody.appendChild(
        template.content.cloneNode(true)
      );


      /*
        Product-specific behaviour.
      */

      this.setupGallery();
      this.setupDescriptionToggle();
      this.setupDetailsFocus();
      this.setupFullProductLink();


      /*
        Always open from top.
      */

      this.quickViewBody.scrollTop =
        0;


      /*
        Show modal.
      */

      this.quickView.hidden =
        false;


      this.lockPage();


      requestAnimationFrame(
        () => {

          requestAnimationFrame(
            () => {

              this.quickView.classList.add(
                'is-open'
              );

            }
          );

        }
      );


      /*
        Accessible focus.
      */

      const close =
        this.quickView.querySelector(
          '[data-quick-view-close].boho-quick-view__close'
        );


      window.setTimeout(
        () => {

          if (!close) {
            return;
          }


          try {

            close.focus({
              preventScroll: true
            });

          } catch (error) {

            close.focus();

          }

        },
        80
      );
    }



    /* ============================================================
       CLOSE
       ============================================================ */

    closeQuickView() {

      if (
        !this.quickView ||
        this.quickView.hidden
      ) {
        return;
      }


      this.quickView.classList.remove(
        'is-open'
      );


      this.unlockPage();


      window.setTimeout(
        () => {

          this.quickView.hidden =
            true;


          this.quickViewBody.innerHTML =
            '';


          if (
            this.lastQuickViewTrigger &&
            document.contains(
              this.lastQuickViewTrigger
            )
          ) {

            try {

              this.lastQuickViewTrigger.focus({
                preventScroll: true
              });

            } catch (error) {

              this.lastQuickViewTrigger.focus();

            }

          }


          this.lastQuickViewTrigger =
            null;

        },
        230
      );
    }



    /* ============================================================
       GALLERY
       ============================================================ */

    setupGallery() {

      const slides =
        Array.from(
          this.quickViewBody.querySelectorAll(
            '[data-qv-slide]'
          )
        );


      const thumbs =
        Array.from(
          this.quickViewBody.querySelectorAll(
            '[data-qv-thumb]'
          )
        );


      const dots =
        Array.from(
          this.quickViewBody.querySelectorAll(
            '[data-qv-dot]'
          )
        );


      const previous =
        this.quickViewBody.querySelector(
          '[data-qv-prev]'
        );


      const next =
        this.quickViewBody.querySelector(
          '[data-qv-next]'
        );


      if (!slides.length) {
        return;
      }


      let activeIndex =
        0;


      const showSlide =
        (
          requestedIndex,
          options = {}
        ) => {

          const {
            source = 'control'
          } = options;


          let index =
            Number(
              requestedIndex
            );


          if (
            Number.isNaN(index)
          ) {
            index = 0;
          }


          if (index < 0) {

            index =
              slides.length - 1;

          }


          if (
            index >=
            slides.length
          ) {

            index = 0;

          }


          activeIndex =
            index;


          slides.forEach(
            (
              slide,
              slideIndex
            ) => {

              const active =
                slideIndex ===
                activeIndex;


              slide.classList.toggle(
                'is-active',
                active
              );


              slide.setAttribute(
                'aria-hidden',
                active
                  ? 'false'
                  : 'true'
              );

            }
          );


          thumbs.forEach(
            (
              thumb,
              thumbIndex
            ) => {

              const active =
                thumbIndex ===
                activeIndex;


              thumb.classList.toggle(
                'is-active',
                active
              );


              thumb.setAttribute(
                'aria-current',
                active
                  ? 'true'
                  : 'false'
              );

            }
          );


          dots.forEach(
            (
              dot,
              dotIndex
            ) => {

              const active =
                dotIndex ===
                activeIndex;


              dot.classList.toggle(
                'is-active',
                active
              );


              dot.setAttribute(
                'aria-current',
                active
                  ? 'true'
                  : 'false'
              );

            }
          );


          /*
            Smoothly follow the active desktop thumbnail.
          */

          if (
            source !== 'initial'
          ) {

            const activeThumb =
              thumbs[activeIndex];


            activeThumb?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            });

          }

        };


      previous?.addEventListener(
        'click',
        (event) => {

          event.preventDefault();
          event.stopPropagation();


          showSlide(
            activeIndex - 1
          );

        }
      );


      next?.addEventListener(
        'click',
        (event) => {

          event.preventDefault();
          event.stopPropagation();


          showSlide(
            activeIndex + 1
          );

        }
      );


      thumbs.forEach(
        (thumb) => {

          thumb.addEventListener(
            'click',
            (event) => {

              event.preventDefault();


              showSlide(
                Number(
                  thumb.dataset.qvIndex ||
                  0
                )
              );

            }
          );

        }
      );


      dots.forEach(
        (dot) => {

          dot.addEventListener(
            'click',
            (event) => {

              event.preventDefault();


              showSlide(
                Number(
                  dot.dataset.qvIndex ||
                  0
                )
              );

            }
          );

        }
      );


      /*
        Mobile swipe.

        Vertical gestures remain available
        for modal scrolling.
      */

      const gallery =
        this.quickViewBody.querySelector(
          '[data-qv-gallery]'
        );


      if (gallery) {

        let startX =
          null;

        let startY =
          null;


        gallery.addEventListener(
          'touchstart',
          (event) => {

            const touch =
              event.touches?.[0];


            if (!touch) {
              return;
            }


            startX =
              touch.clientX;

            startY =
              touch.clientY;

          },
          {
            passive: true
          }
        );


        gallery.addEventListener(
          'touchend',
          (event) => {

            if (
              startX === null ||
              startY === null
            ) {
              return;
            }


            const touch =
              event.changedTouches?.[0];


            if (!touch) {

              startX = null;
              startY = null;

              return;
            }


            const deltaX =
              touch.clientX -
              startX;


            const deltaY =
              touch.clientY -
              startY;


            const horizontalGesture =
              Math.abs(deltaX) >= 40 &&
              Math.abs(deltaX) >
                Math.abs(deltaY) * 1.25;


            if (horizontalGesture) {

              if (deltaX < 0) {

                showSlide(
                  activeIndex + 1,
                  {
                    source: 'swipe'
                  }
                );

              } else {

                showSlide(
                  activeIndex - 1,
                  {
                    source: 'swipe'
                  }
                );

              }

            }


            startX =
              null;

            startY =
              null;

          },
          {
            passive: true
          }
        );

      }


      showSlide(
        0,
        {
          source: 'initial'
        }
      );
    }



    /* ============================================================
       DESCRIPTION — VIEW MORE / VIEW LESS
       ============================================================ */

    setupDescriptionToggle() {

      const description =
        this.quickViewBody.querySelector(
          '.boho-quick-view__description'
        );


      const detailsContent =
        this.quickViewBody.querySelector(
          '.boho-quick-view__details-content'
        );


      if (
        !description ||
        !detailsContent
      ) {
        return;
      }


      /*
        The Liquid already contains the full product
        description inside Product Details.

        Use that as the source instead of requiring
        another Liquid change.
      */

      const fullDescription =
        detailsContent.textContent
          .replace(/\s+/g, ' ')
          .trim();


      if (!fullDescription) {
        return;
      }


      /*
        Replace truncated Liquid copy with full text.
        CSS keeps it visually clamped by default.
      */

      description.textContent =
        fullDescription;


      /*
        Don't show View more for genuinely short copy.
      */

      if (
        fullDescription.length <= 135
      ) {
        return;
      }


      const wrapper =
        document.createElement(
          'div'
        );


      wrapper.className =
        'boho-quick-view__description-wrap';


      description.parentNode.insertBefore(
        wrapper,
        description
      );


      wrapper.appendChild(
        description
      );


      const toggle =
        document.createElement(
          'button'
        );


      toggle.type =
        'button';


      toggle.className =
        'boho-quick-view__description-toggle';


      toggle.textContent =
        'View more';


      toggle.setAttribute(
        'aria-expanded',
        'false'
      );


      wrapper.appendChild(
        toggle
      );


      toggle.addEventListener(
        'click',
        () => {

          const expanded =
            description.classList.toggle(
              'is-expanded'
            );


          toggle.textContent =
            expanded
              ? 'View less'
              : 'View more';


          toggle.setAttribute(
            'aria-expanded',
            expanded
              ? 'true'
              : 'false'
          );

        }
      );
    }



    /* ============================================================
       PRODUCT DETAILS AUTO-FOCUS
       ============================================================ */

    setupDetailsFocus() {

      const details =
        this.quickViewBody.querySelector(
          '[data-qv-details]'
        );


      if (!details) {
        return;
      }


      details.addEventListener(
        'toggle',
        () => {

          if (!details.open) {
            return;
          }


          window.setTimeout(
            () => {

              const container =
                this.quickViewBody;


              const containerRect =
                container.getBoundingClientRect();


              const detailsRect =
                details.getBoundingClientRect();


              const currentOffset =
                detailsRect.top -
                containerRect.top;


              const target =
                container.scrollTop +
                currentOffset -
                18;


              container.scrollTo({
                top: Math.max(
                  0,
                  target
                ),
                behavior: 'smooth'
              });

            },
            120
          );

        }
      );
    }



    /* ============================================================
       FULL PRODUCT LINK
       ============================================================ */

    setupFullProductLink() {

      const link =
        this.quickViewBody.querySelector(
          '[data-qv-full-product]'
        );


      if (!link) {
        return;
      }


      link.addEventListener(
        'click',
        (event) => {

          /*
            Preserve:
            Cmd click
            Ctrl click
            Shift click
            Alt click
            middle click
          */

          if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0
          ) {
            return;
          }


          const destination =
            link.href;


          if (!destination) {
            return;
          }


          event.preventDefault();


          if (
            link.classList.contains(
              'is-loading'
            )
          ) {
            return;
          }


          const label =
            link.querySelector(
              '[data-qv-full-label]'
            );


          const arrow =
            link.querySelector(
              '[data-qv-full-arrow]'
            );


          const loader =
            link.querySelector(
              '[data-qv-full-loader]'
            );


          link.classList.add(
            'is-loading'
          );


          link.setAttribute(
            'aria-disabled',
            'true'
          );


          if (label) {

            label.dataset.originalLabel =
              label.textContent.trim();


            label.textContent =
              'Opening…';

          }


          if (arrow) {
            arrow.hidden = true;
          }


          if (loader) {
            loader.hidden = false;
          }


          window.setTimeout(
            () => {

              window.location.assign(
                destination
              );

            },
            180
          );

        }
      );
    }



    /* ============================================================
       EVENTS
       ============================================================ */

    bindEvents() {

      this.root.addEventListener(
        'click',
        (event) => {

          const trigger =
            event.target.closest(
              '[data-quick-view-trigger]'
            );


          if (
            trigger &&
            this.root.contains(
              trigger
            )
          ) {

            event.preventDefault();
            event.stopPropagation();


            this.openQuickView(
              trigger
            );


            return;
          }


          const close =
            event.target.closest(
              '[data-quick-view-close]'
            );


          if (
            close &&
            this.root.contains(
              close
            )
          ) {

            event.preventDefault();


            this.closeQuickView();

          }

        }
      );


      document.addEventListener(
        'keydown',
        (event) => {

          if (
            event.key !== 'Escape'
          ) {
            return;
          }


          if (
            this.quickView &&
            !this.quickView.hidden
          ) {

            this.closeQuickView();

          }

        }
      );

    }

  }



  /* ============================================================
     RESET NAVIGATION LOADERS
     Important for browser Back / bfcache.
     ============================================================ */

  function resetFullProductLoaders(
    container = document
  ) {

    container
      .querySelectorAll(
        '[data-qv-full-product].is-loading'
      )
      .forEach(
        (link) => {

          link.classList.remove(
            'is-loading'
          );


          link.removeAttribute(
            'aria-disabled'
          );


          const label =
            link.querySelector(
              '[data-qv-full-label]'
            );


          const arrow =
            link.querySelector(
              '[data-qv-full-arrow]'
            );


          const loader =
            link.querySelector(
              '[data-qv-full-loader]'
            );


          if (label) {

            label.textContent =
              label.dataset.originalLabel ||
              'View full product';

          }


          if (arrow) {
            arrow.hidden = false;
          }


          if (loader) {
            loader.hidden = true;
          }

        }
      );

  }


  window.addEventListener(
    'pageshow',
    () => {

      resetFullProductLoaders(
        document
      );

    }
  );



  /* ============================================================
     INITIALIZE
     ============================================================ */

  function initializeWithin(
    container = document
  ) {

    const roots =
      [];


    if (
      container.matches &&
      container.matches(
        ROOT_SELECTOR
      )
    ) {

      roots.push(
        container
      );

    }


    if (
      container.querySelectorAll
    ) {

      roots.push(
        ...container.querySelectorAll(
          ROOT_SELECTOR
        )
      );

    }


    roots.forEach(
      (root) => {

        if (
          root.dataset
            .bohoQuickViewInitialized ===
          'true'
        ) {
          return;
        }


        root.dataset
          .bohoQuickViewInitialized =
          'true';


        new BohoQuickView(
          root
        );

      }
    );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {

        initializeWithin(
          document
        );

      }
    );

  } else {

    initializeWithin(
      document
    );

  }



  /* ============================================================
     SHOPIFY THEME EDITOR
     ============================================================ */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initializeWithin(
        event.target
      );

    }
  );

})();